import type { ColumnRow } from "../../ColumnSelectionGrid.tsx";
import type { TreeColumnType } from "../../ColumnSelectionPopover.tsx";

/**
 * Merges the saved column order with a template-provided row snapshot while keeping the template
 * as the source of truth for grouping. Existing rows take precedence when the template omits them.
 */
export function mergeColumnRows(allRows: ColumnRow[] | null, templateRows: ColumnRow[] | null): ColumnRow[] | null {
  if (!allRows && !templateRows) {
    return null;
  }

  // When only templates are provided we still need to clear empty groups before returning.
  if (!allRows) {
    return templateRows ? removeEmptyGroups(templateRows) : null;
  }

  if (!templateRows) {
    return allRows;
  }

  // Track both sources by a composite key so we can resolve conflicts quickly during the merge.
  // We assume the server does not provide duplicate column identifiers (aside from distinct group variants),
  // so template order can drive the final output without heavy de-duplication logic.
  const rowsByKey = new Map<string, ColumnRow>();
  const templateRowsByKey = new Map<string, ColumnRow>();

  for (const row of allRows) {
    rowsByKey.set(getRowKey(row), row);
  }

  for (const templateRow of templateRows) {
    templateRowsByKey.set(getRowKey(templateRow), templateRow);
  }

  const orderedRows: ColumnRow[] = [];
  const remainingAllRows = new Map(rowsByKey);
  const resolvedGroups = new Map<string, ColumnRow>();

  const addRow = (row: ColumnRow, key: string) => {
    // Preserve ordering by pushing rows exactly once and marking the composite key as consumed.
    orderedRows.push(row);

    if (row.isGroup) {
      // Cache resolved groups to avoid constructing duplicates later in the merge.
      resolvedGroups.set(key, row);
    }

    // Remove the emitted row from the pool so we can append only the leftovers afterwards.
    remainingAllRows.delete(key);
  };

  const ensureGroupRow = (parentId: string | undefined): string | undefined => {
    if (!parentId) {
      return undefined;
    }

    const groupKey = getGroupKey(parentId);
    const existingGroup = resolvedGroups.get(groupKey);

    if (existingGroup) {
      // If the group already exists just share its id with the caller.
      return existingGroup.id ?? parentId;
    }

    const referenceGroup = rowsByKey.get(groupKey);
    const templateGroup = templateRowsByKey.get(groupKey);

    if (!referenceGroup && !templateGroup) {
      return undefined;
    }

    const mergedGroup = mergeGroupRow(referenceGroup, templateGroup ?? referenceGroup);

    if (!mergedGroup) {
      // A group without a resolvable identifier cannot be used; skip silently.
      return undefined;
    }

    addRow(mergedGroup, groupKey);

    return mergedGroup.id ?? parentId;
  };

  for (const row of templateRows) {
    const key = getRowKey(row);

    const referenceRow = rowsByKey.get(key);

    // Template group rows act as the canonical definitions when available.
    if (row.isGroup) {
      if (resolvedGroups.has(key)) {
        continue;
      }

      const mergedGroup = mergeGroupRow(referenceRow, row);

      if (!mergedGroup) {
        continue;
      }

      addRow(mergedGroup, key);
      continue;
    }

    // Template rows are expected to exist in allRows but fall back to the template definition if missing.
    const resolvedParentId =
      ensureGroupRow(row.parentId) ??
      (referenceRow?.parentId ? (ensureGroupRow(referenceRow.parentId) ?? referenceRow.parentId) : undefined);

    addRow(mergeLeafRow(referenceRow ?? row, row, resolvedParentId), key);
  }

  for (const row of allRows) {
    const key = getRowKey(row);

    if (!remainingAllRows.has(key)) {
      continue;
    }

    if (row.parentId) {
      // Rebasing a previously unseen child still requires creating its parent first.
      ensureGroupRow(row.parentId);
    }

    addRow(row, key);
  }

  return removeEmptyGroups(orderedRows);
}

// Converts the hierarchical AG Grid tree definition into our flat row format for later merging.
export function columnsToRows(columns: TreeColumnType[] | null): ColumnRow[] | null {
  if (!columns) {
    return null;
  }

  const rows: ColumnRow[] = [];

  for (const column of columns) {
    const parentColId = column.colId;
    if (!parentColId) {
      continue;
    }

    const parentTitle = column.headerName ?? parentColId;

    const children =
      column.children?.filter(
        (child): child is TreeColumnType & { colId: string } => child != null && typeof child.colId === "string",
      ) ?? [];
    const hasChildren = children.length > 0;

    if (!hasChildren) {
      // The column is standalone; persist as a leaf row directly.
      rows.push({
        id: parentColId,
        title: parentTitle,
        colId: parentColId,
        hide: column.hide,
        fixed: column.fixed,
      });
      continue;
    }

    const groupRowId = `${parentColId}::${children
      .map((child) => child.colId)
      .sort()
      .join("|")}`;

    rows.push({
      id: groupRowId,
      groupName: parentColId,
      isGroup: true,
      title: parentTitle,
      colId: parentColId,
      hide: column.hide,
      fixed: column.fixed,
    });

    for (const child of children) {
      const childColId = child.colId;

      rows.push({
        id: childColId,
        groupName: parentColId,
        parentId: groupRowId,
        title: child.headerName ?? childColId,
        colId: childColId,
        hide: child.hide,
        fixed: child.fixed,
      });
    }
  }

  return rows;
}

// Combines metadata for a group row, preferring identifiers from the template while falling back to the grid data.
function mergeGroupRow(referenceGroup: ColumnRow | undefined, templateGroup: ColumnRow | undefined) {
  const source = templateGroup ?? referenceGroup;

  if (!source) {
    return undefined;
  }

  const resolvedId =
    templateGroup?.id ??
    referenceGroup?.id ??
    templateGroup?.colId ??
    referenceGroup?.colId ??
    templateGroup?.title ??
    referenceGroup?.title;

  if (!resolvedId) {
    // Cannot safely merge a group without a persistent identifier.
    return undefined;
  }

  const resolvedTitle =
    templateGroup?.title ?? referenceGroup?.title ?? templateGroup?.colId ?? referenceGroup?.colId ?? resolvedId;

  return {
    ...referenceGroup,
    ...templateGroup,
    id: resolvedId,
    isGroup: true,
    title: resolvedTitle,
  };
}

// Applies template overrides to a leaf row and keeps parent linkage consistent with newly generated groups.
function mergeLeafRow(referenceRow: ColumnRow, templateRow: ColumnRow, parentId: string | undefined): ColumnRow {
  const mergedRow: ColumnRow = {
    ...referenceRow,
    ...templateRow,
  };

  if (!mergedRow.title) {
    mergedRow.title = templateRow.title ?? referenceRow.title;
  }

  if (parentId) {
    // Parent was re-created or remapped, ensure the child points to the new identifier.
    mergedRow.parentId = parentId;
  } else {
    delete mergedRow.parentId;
  }

  if (mergedRow.parentId == null) {
    mergedRow.groupName = templateRow.groupName ?? referenceRow.groupName;
  } else if (templateRow.groupName != null) {
    mergedRow.groupName = templateRow.groupName;
  } else if (referenceRow.groupName != null) {
    mergedRow.groupName = referenceRow.groupName;
  } else {
    delete mergedRow.groupName;
  }

  return mergedRow;
}

// Removes any group entries that would otherwise render as empty shells after merging.
function removeEmptyGroups(rows: ColumnRow[]): ColumnRow[] {
  const childCount = new Map<string, number>();

  for (const row of rows) {
    if (!row.parentId) {
      continue;
    }

    childCount.set(row.parentId, (childCount.get(row.parentId) ?? 0) + 1);
  }

  return rows.filter((row) => {
    if (!row.isGroup) {
      return true;
    }

    if (!row.id) {
      return false;
    }

    return (childCount.get(row.id) ?? 0) > 0;
  });
}

// Generates the key we use to track rows originating from different sources.
function getRowKey(row: ColumnRow): string {
  const baseKey = row.id ?? row.colId ?? row.title;
  const scope = row.isGroup ? "group" : "column";

  return `${scope}:${baseKey}`;
}

// Helpers to namespace group/column identifiers when caching/looking them up.
function getGroupKey(groupId: string): string {
  return `group:${groupId}`;
}
